import React from "react";

/**
 * Reusable hero section — full-bleed photo with slow Ken Burns and a darker
 * bottom gradient to anchor headline legibility.
 */
export default function HeroSection({
  image,
  imageAlt = "",
  eyebrow,
  title,
  italicTail,
  subtitle,
  height = "h-[75vh]",
  align = "center",
  children,
}) {
  const alignClass =
    align === "left"
      ? "items-start text-left"
      : align === "right"
        ? "items-end text-right"
        : "items-center text-center";
  return (
    <section
      data-testid="hero-section"
      className={`relative ${height} min-h-[520px] w-full overflow-hidden bg-charcoal text-ivory`}
    >
      {image && (
        <img
          src={image}
          alt={imageAlt || title || ""}
          className="absolute inset-0 h-full w-full object-cover page-hero-kb"
          loading="eager"
        />
      )}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,15,15,0.30) 0%, rgba(15,15,15,0.0) 35%, rgba(15,15,15,0.0) 55%, rgba(15,15,15,0.70) 100%)",
        }}
        aria-hidden
      />
      <div className="relative z-10 h-full">
        <div className={`mx-auto max-w-7xl h-full px-6 md:px-10 flex flex-col justify-end pb-24 md:pb-32 ${alignClass}`}>
          <div className={`max-w-3xl hero-legible ${alignClass}`}>
            {eyebrow && (
              <p
                className="eyebrow text-brass mb-5 animate-rise-in"
                style={{ animationDelay: "200ms", opacity: 0 }}
                data-testid="hero-eyebrow"
              >
                {eyebrow}
              </p>
            )}
            {title && (
              <h1
                className="font-display text-ivory font-normal leading-[1.04] tracking-tight animate-rise-in text-balance"
                style={{ fontSize: "clamp(2.75rem, 7vw, 6rem)", animationDelay: "350ms", opacity: 0 }}
                data-testid="hero-headline"
              >
                {title}
                {italicTail && (
                  <>
                    <br />
                    <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>
                      {italicTail}
                    </span>
                  </>
                )}
              </h1>
            )}
            {subtitle && (
              <p
                className="italic-quote text-ivory/85 mt-5 animate-rise-in text-balance"
                style={{ fontSize: "clamp(1.15rem, 1.6vw, 1.5rem)", animationDelay: "550ms", opacity: 0, maxWidth: "46ch", lineHeight: 1.45 }}
                data-testid="hero-subtitle"
              >
                {subtitle}
              </p>
            )}
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}
